import { useEffect, useState } from "react";
import { FolderOpen, Bell, Flame, User, Plus } from "lucide-react";
import api from "../../lib/api";
import { eur } from "../../lib/format";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { ActionTimeline } from "../actions/ActionLogDialog";

const PRIO = {
  HAUTE: "bg-red-500/10 text-red-400 border-red-500/20",
  MOYENNE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  BASSE: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

export default function ProjectDetailSheet({ projectId, open, onOpenChange, onLogAction, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && projectId) {
      setData(null);
      api.get(`/projects/${projectId}`).then((r) => setData(r.data)).catch(() => setData(null));
    }
  }, [open, projectId, refreshKey]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0B0F17] border-[#1E293B] text-slate-100 w-full sm:max-w-lg overflow-y-auto" data-testid="project-detail-sheet">
        {!data ? (
          <>
            <SheetHeader>
              <SheetTitle className="sr-only">Détail du projet</SheetTitle>
            </SheetHeader>
            <div className="text-slate-500 mt-6">Chargement du projet…</div>
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-head text-xl text-slate-100 flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-amber-400" /> {data.project.titre || "Projet"}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{data.project.statut}</span>
                {data.project.famille && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-700/40 text-slate-300">{data.project.famille}</span>}
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${PRIO[data.priorite]}`} data-testid="project-priorite">
                  {data.priorite === "HAUTE" && <Flame className="h-3 w-3" />} {data.priorite}
                </span>
              </div>

              <button
                data-testid="project-goto-client"
                onClick={() => onOpenChange(false)}
                className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300"
              >
                <User className="h-3.5 w-3.5" /> {data.project.client_nom}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-[#111827] border border-[#1E293B] p-3">
                  <div className="text-[11px] text-slate-500">Montant total</div>
                  <div className="font-mono-metric text-lg font-bold text-sky-400" data-testid="project-montant">{eur(data.montant_total)}</div>
                </div>
                <div className="rounded-lg bg-[#111827] border border-[#1E293B] p-3">
                  <div className="text-[11px] text-slate-500">Prochaine relance</div>
                  <div className={`font-mono-metric text-lg font-bold ${data.jours_retard > 0 ? "text-red-400" : "text-slate-100"}`} data-testid="project-prochaine-relance">
                    {data.prochaine_relance || "—"}
                  </div>
                  {data.jours_retard > 0 && <div className="text-[10px] text-red-400">+{data.jours_retard}j de retard</div>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                <div>Dernière action : <span className="text-slate-200">{data.derniere_action ? (data.derniere_action.date || "").slice(0, 10) : "—"}</span></div>
                <div>Dernière relance : <span className="text-slate-200">{data.derniere_relance || "—"}</span></div>
              </div>

              <Button
                data-testid="project-log-action"
                onClick={() => onLogAction({ client_id: data.project.client_id, project_id: data.project.id, client_nom: data.project.client_nom, devis_id: (data.devis[0] || {}).id })}
                className="w-full bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold"
              >
                <Plus className="h-4 w-4 mr-1" /> Enregistrer une action
              </Button>

              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Bell className="h-4 w-4 text-slate-400" /> Historique des actions</h3>
                <ActionTimeline actions={data.actions} />
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Dossiers du projet</h3>
                <div className="space-y-2">
                  {data.devis.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-lg bg-[#111827] border border-[#1E293B] p-3">
                      <div>
                        <div className="text-sm font-medium">{d.famille || d.titre}</div>
                        <div className="text-xs text-slate-500 font-mono-metric">{d.numero} · {d.statut}</div>
                      </div>
                      <span className="font-mono-metric text-sm font-semibold">{eur(d.montant_total ?? d.total_ht)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
